import { useFormik } from 'formik';
import React from 'react';
import * as Yup from 'yup';
import MultiselectCheckbox from './inputs/multiselectCheckbox';

const initialValues = {
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    preferredLocation: '',
    locationName: '',
    preferredDay: [],
    preferredTime: [],
    reason: '',
    optIn: false,
};

const validationSchema = Yup.object().shape({
    firstName: Yup.string().required('Required').max(40, 'Too long'),
    lastName: Yup.string().required('Required').max(40, 'Too long'),
    email: Yup.string().required('Required').email('Invalid Email'),
    mobile: Yup.string().required('Required').matches(/^[0-9]{10}$/, 'Invalid mobile number. It should be 10 digits long.'),
    preferredLocation: Yup.string().required('Required'),
    preferredDay: Yup.array().min(1, 'At least one day should be selected'),
    preferredTime: Yup.array().min(1, 'At least one time slot should be selected'),
    reason: Yup.string().max(600, 'Reason too long'),
});

const locations = [{ text: '', value: '' },
{ text: 'Abbotsford, BC', value: 'abbotsford' },
{ text: 'Kamloops, BC', value: 'kamloops' },
{ text: 'Kelowna, BC', value: 'kelowna' },
{ text: 'Prince George, BC', value: 'prince_george' },
{ text: 'Airdrie, AB', value: 'airdrie' },
{ text: 'Edmonton, AB', value: 'edmonton' },
{ text: 'Lacombe, AB', value: 'lacombe' },
{ text: 'Leduc, AB', value: 'leduc' },
{ text: 'Bedford, NS', value: 'bedford' },
{ text: 'Cookstown, ON', value: 'cookstown' },
{ text: 'Whitehorse, YT', value: 'whitehorse' }];

const ServiceForm = ({ onSubmit, disabled, errorMsg }) => {
    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: (formData) => onSubmit({ ...formData, locationName: locations.find(({ value }) => value === formData.preferredLocation)?.text })
    });

    const getErrorMsg = (name) => {
        return formik.touched[name] && formik.errors[name] ? (
            <div style={{ color: 'red' }}>{formik.errors[name]}</div>
        ) : null;
    };

    const getFormGroupClass = (fieldName, override) => {
        const defaultClassName = override || 'form-group';
        return formik.touched[fieldName] && formik.errors[fieldName] ? `${defaultClassName} invalid` : `${defaultClassName}`;
    };

    return (
        <div className='container service-form my-4'>
            <h3>Book Your Service Appointment</h3>
            {errorMsg ? <p className='alert alert-danger'>{errorMsg}</p> : null}
            <form onSubmit={formik.handleSubmit}>
                <div className="form-row">
                    <div className={getFormGroupClass('firstName') + " col-12 col-md-6"}>
                        <label htmlFor="firstName">First Name</label>
                        <input
                            className="form-control"
                            id="firstName"
                            name="firstName"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.firstName}
                        />
                        {getErrorMsg('firstName')}
                    </div>

                    <div className={getFormGroupClass('lastName') + " col-12 col-md-6"}>
                        <label htmlFor="lastName">Last Name</label>
                        <input
                            className="form-control"
                            id="lastName"
                            name="lastName"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.lastName}
                        />
                        {getErrorMsg('lastName')}
                    </div>

                    <div className={getFormGroupClass('email') + " col-12"}>
                        <label htmlFor="email">Email</label>
                        <input
                            className="form-control"
                            id="email"
                            name="email"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.email}
                        />
                        {getErrorMsg('email')}
                    </div>

                    <div className={getFormGroupClass('mobile') + " col-12"}>
                        <label htmlFor="mobile">Mobile</label>
                        <input
                            className="form-control"
                            id="mobile"
                            name="mobile"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.mobile}
                        />
                        {getErrorMsg('mobile')}
                    </div>

                    <div className={getFormGroupClass('preferredLocation') + " col-12"}>
                        <label htmlFor="preferredLocation">Preferred Location</label>
                        <select
                            className="form-control"
                            id="preferredLocation"
                            name="preferredLocation"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.preferredLocation}
                        >
                            {locations.map(({ text, value }, index) => (
                                <option key={index} value={value}>
                                    {text}
                                </option>
                            ))}
                        </select>
                        {getErrorMsg('preferredLocation')}
                    </div>

                    <div className={getFormGroupClass('preferredDay', '') + " col-12"}>
                        <label htmlFor="preferredDay">Preferred Day (select one or more)</label>
                        <MultiselectCheckbox
                            className="d-flex flex-wrap gap-2"
                            id="preferredDay"
                            name="preferredDay"
                            onChange={v => formik.setFieldValue('preferredDay', v)}
                            onBlur={formik.handleBlur}
                            value={formik.values.preferredDay}
                            options={[
                                'Monday',
                                'Tuesday',
                                'Wednesday',
                                'Thursday',
                                'Friday',
                                'Saturday',
                                'Sunday',
                            ]}
                        >
                        </MultiselectCheckbox>
                        {getErrorMsg('preferredDay')}
                    </div>

                    <div className={getFormGroupClass('preferredTime', '') + " col-12"}>
                        <label htmlFor="preferredTime">Preferred Time (select one or more)</label>
                        <MultiselectCheckbox
                            className="d-flex flex-wrap gap-2"
                            id="preferredTime"
                            name="preferredTime"
                            multiple
                            onChange={v => formik.setFieldValue('preferredTime', v)}
                            onBlur={formik.handleBlur}
                            value={formik.values.preferredTime}
                            options={['Morning', 'Mid-afternoon', 'Late afternoon']}
                        >
                        </MultiselectCheckbox>
                        {getErrorMsg('preferredTime')}
                    </div>

                    <div className={getFormGroupClass('reason') + " col-12"}>
                        <label htmlFor="reason">Appointment Reason/Type of Service</label>
                        <textarea
                            className="form-control"
                            id="reason"
                            name="reason"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.reason}
                        />
                        {getErrorMsg('reason')}
                    </div>

                    <div className='form-group'>
                        <div className='form-check'>

                            <input
                                type='checkbox'
                                className="form-check-input "
                                id="optIn"
                                name="optIn"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value='optIn'
                            />
                            <label className="form-check-label" for="optIn">
                                Yes, I consent to receive promotional emails from Fraserway RV. I understand that I can unsubscribe at any time.
                            </label>



                            {getErrorMsg('optIn')}
                        </div>
                    </div>
                </div>

                <button type="submit" disabled={disabled} className='btn btn-primary'>Submit</button>
            </form>
        </div>
    );
};

export default ServiceForm;
